import { Element, ElementType } from '@ritabygglov/core';
import { useAppState } from '../../context/AppState/useAppState';
import { useMouseEvents } from '../../context/MouseEvents/useMouseEvents';
import { computeMultiselectBBox } from '../../helpers/utility';
import { useMeasurement } from '../../hooks/useMeasurement';
import { SelectionModeHelper, SelectionModes } from '../../types';
import Cursors from '../Cursors/Cursors';
import { NorthArrow } from '../NorthArrow/NorthArrow';
import { RenderMap } from '../RenderMap/RenderMap';
import { TitleBlock } from '../TitleBlock/TitleBlock';
import { Watermark } from '../Watermark/Watermark';
import './Canvas.css';
import { CanvasLoadingProgress } from './CanvasLoadingProgress';
import { CanvasElements } from './CanvasElements';
import { DrawBackgroundLines, MultiSelectBox } from './CanvasUtils';
import { MultiSelectBoundingBox } from './MultiSelectBoundingBox';
import { DebugSnaplines } from './DebugSnaplines';
import { Patterns } from './Patterns';
import { SnapLines } from './Snaplines';

const optionalStyle = (isActive: boolean, className: string) =>
  isActive ? className : '';

const Canvas = () => {
  const {
    appState,
    selectedElements,
    hoverElement,
    selectionMode,
    showDebugger,
    showAllSnaplines,
    viewBox,
    session,
    selectionCoordinates,
    isSpacePressed,
    snapLines,
    snapPoints,
    isCanvasReady,
    loadProgress,
  } = useAppState();
  const { onMouseOver, onMouseDown, onMouseMove, onMouseUp } = useMouseEvents();
  const { scalingFactor } = useMeasurement();

  if (!isCanvasReady) {
    return <CanvasLoadingProgress progress={loadProgress} />;
  }

  const { elements } = appState;

  const renderElements = (
    <CanvasElements
      elements={elements}
      selectedElements={selectedElements}
      hoverElement={hoverElement}
      selectionMode={selectionMode}
      session={session}
      scalingFactor={scalingFactor}
      viewBox={viewBox}
    />
  );

  const isAdding = selectionMode.type === SelectionModes.Add;
  const isPanning = SelectionModeHelper.isPanning({
    selectionMode,
    isSpacePressed,
  });
  const isMoving = selectionMode.type === SelectionModes.Selected;
  const isRadiusDragging = selectionMode.type === SelectionModes.RadiusDragging;
  const showMultiSelect = selectionMode.type === SelectionModes.MultiSelecting;
  const isRubberbanding =
    showMultiSelect &&
    selectionCoordinates.startX !== null &&
    selectionCoordinates.currentX !== null;
  const soleSelectedElement =
    selectedElements.length === 1 ? elements[selectedElements[0]] : undefined;
  const isSingleGroupSelected =
    soleSelectedElement?.type === ElementType.Group &&
    selectionMode.type !== SelectionModes.GroupEditing;

  // A persisted Group's own x/y/width/height/rotate ARE its bounding box —
  // authoritative, no recompute needed. Ad-hoc multiselect has no persisted
  // frame, so its bbox is still derived live from the selected elements'
  // (axis-aligned) corners, and always renders unrotated.
  const boundingBox =
    soleSelectedElement?.type === ElementType.Group && isSingleGroupSelected
      ? {
          x: soleSelectedElement.x,
          y: soleSelectedElement.y,
          width: soleSelectedElement.width,
          height: soleSelectedElement.height,
        }
      : computeMultiselectBBox(
          selectedElements
            .map((id) => elements[id])
            .filter((e): e is Element => e !== undefined)
        );
  const boundingBoxRotate =
    soleSelectedElement?.type === ElementType.Group && isSingleGroupSelected
      ? soleSelectedElement.rotate
      : 0;

  const showMultiselectBoundingBox =
    (selectedElements.length > 1 || isSingleGroupSelected) &&
    boundingBox !== null &&
    // Ad-hoc multiselect has no persisted rotation, so its axis-aligned box
    // would look wrong spinning with the elements — hide it mid-rotation.
    // A persisted group's box rotates rigidly with it, so it stays visible.
    (isSingleGroupSelected || selectionMode.type !== SelectionModes.Rotating) &&
    !isRubberbanding;
  return (
    <svg
      // eslint-disable-next-line react/no-unknown-property
      data-xmlns="http://www.w3.org/2000/svg"
      className={`canvas
        ${optionalStyle(isAdding, 'isAdding')}
        ${optionalStyle(isPanning, 'isPanning')}
        ${optionalStyle(isMoving, 'isMoving')}
        ${optionalStyle(isRadiusDragging, 'isRadiusDragging')}`}
      id="container"
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseMove={onMouseMove}
      onMouseOver={onMouseOver}
      viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
      width={window.innerWidth}
      height={window.innerHeight}
      preserveAspectRatio={'xMinYMin meet'}
      data-testid="canvas"
      data-export-ready={isCanvasReady ? 'true' : undefined}
    >
      <Patterns />
      <RenderMap />
      {showDebugger && <DrawBackgroundLines />}
      {renderElements}

      <TitleBlock />
      <NorthArrow />
      {showMultiSelect && (
        <MultiSelectBox selectionCoordinates={selectionCoordinates} />
      )}
      {showMultiselectBoundingBox && boundingBox && (
        <MultiSelectBoundingBox
          bbox={boundingBox}
          rotate={boundingBoxRotate}
          scale={viewBox.scale}
        />
      )}
      <Cursors cursors={Object.values(appState.cursors)} />
      {showAllSnaplines && (
        <DebugSnaplines elements={elements} scale={viewBox.scale} />
      )}
      <SnapLines
        snapLines={snapLines}
        snapPoints={snapPoints}
        scale={viewBox.scale}
      />
      <Watermark />
    </svg>
  );
};

export default Canvas;
